import { fetchSenderAutoCampaign, type SenderAutoCampaignView } from '@/api/sender-auto-campaign';
import { fetchSenderConsoleSummary, type SenderConsoleSummary } from '@/api/sender-console';
import { fetchSenderPresets, type SenderRunPreset } from '@/api/sender-presets';
import { fetchPanelSenderSessions } from '@/api/sender-sessions';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { $features } from '@/entities/features-entity';
import type { RunSummary } from '@/entities/sender.entity';
import { countPrependedRuns, useSenderRunsFeed } from '@/hooks/use-sender-runs-feed';
import { senderHref, senderRoutes } from '@/lib/sender-routes';
import SenderCallout from '@/pages/sender/components/sender-callout';
import SenderHero from '@/pages/sender/components/sender-hero';
import SenderLoadError from '@/pages/sender/components/sender-load-error';
import SenderMetricGrid from '@/pages/sender/components/sender-metric-grid';
import SenderRunsVirtualFeed, {
	measurePrependedRunsHeight,
} from '@/pages/sender/components/sender-runs-virtual-feed';
import SenderStepTimeline from '@/pages/sender/components/sender-step-timeline';
import { SenderScreen, senderPrimaryButtonClassName } from '@/pages/sender/sender-screen';
import { MoonIcon, RadioIcon, ZapIcon } from 'lucide-react';
import {
	formatNowCount,
	isNowFirstVisit,
	isNowOptionalFetchFatal,
	nowQueueCount,
	readHttpStatus,
	resolveNowAttention,
	resolveNowPrimaryAction,
	resolveNowSentence,
} from '@/pages/sender/now-state';
import { useUnit } from 'effector-react';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';

const FIRST_STEPS_WITH_PRESETS = ['Сценарий', 'Сессии', 'Запуск'] as const;
const FIRST_STEPS_WITHOUT_PRESETS = ['Сессии', 'Запуск'] as const;
const SCROLL_COMPENSATION_THRESHOLD = 80;

export default function SenderNowPage() {
	const navigate = useNavigate();
	const scrollRef = useRef<HTMLDivElement | null>(null);
	const listAnchorRef = useRef<HTMLDivElement | null>(null);
	const prevRunsRef = useRef<RunSummary[]>([]);
	const { senderConsoleEnabled, senderAutoCampaignEnabled, senderPresetsEnabled } = useUnit($features);
	const {
		runs,
		loading: runsLoading,
		error: runsError,
		hasMore,
		isFetchingMore,
		loadMoreError,
		loadMore,
		retry: retryRuns,
	} = useSenderRunsFeed();
	const [summary, setSummary] = useState<SenderConsoleSummary | null>(null);
	const [autoRunning, setAutoRunning] = useState(false);
	const [queueFailed, setQueueFailed] = useState(0);
	const [scenarioCount, setScenarioCount] = useState(0);
	const [readyAccountCount, setReadyAccountCount] = useState(0);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(false);

	const applyLoaded = useCallback(
		(
			summaryResult: PromiseSettledResult<SenderConsoleSummary | null>,
			autoResult: PromiseSettledResult<SenderAutoCampaignView | null>,
			presetResult: PromiseSettledResult<SenderRunPreset[]>,
			sessionResult: PromiseSettledResult<Awaited<ReturnType<typeof fetchPanelSenderSessions>>>,
		) => {
			const optionalFailed = [
				isNowOptionalFetchFatal({
					enabled: senderConsoleEnabled,
					rejected: summaryResult.status === 'rejected',
					httpStatus: summaryResult.status === 'rejected' ? readHttpStatus(summaryResult.reason) : null,
				}),
				isNowOptionalFetchFatal({
					enabled: senderAutoCampaignEnabled,
					rejected: autoResult.status === 'rejected',
					httpStatus: autoResult.status === 'rejected' ? readHttpStatus(autoResult.reason) : null,
				}),
				isNowOptionalFetchFatal({
					enabled: senderPresetsEnabled,
					rejected: presetResult.status === 'rejected',
					httpStatus: presetResult.status === 'rejected' ? readHttpStatus(presetResult.reason) : null,
				}),
			].some(Boolean);
			if (optionalFailed || sessionResult.status === 'rejected') {
				setError(true);
				setLoading(false);
				return;
			}
			setError(false);
			setSummary(summaryResult.status === 'fulfilled' ? summaryResult.value : null);
			if (autoResult.status === 'fulfilled' && autoResult.value) {
				setAutoRunning(autoResult.value.status === 'running');
				setQueueFailed(autoResult.value.queueFailed);
			} else {
				setAutoRunning(false);
				setQueueFailed(0);
			}
			setScenarioCount(presetResult.status === 'fulfilled' ? presetResult.value.length : 0);
			setReadyAccountCount(sessionResult.status === 'fulfilled' ? sessionResult.value.length : 0);
			setLoading(false);
		},
		[senderAutoCampaignEnabled, senderConsoleEnabled, senderPresetsEnabled],
	);

	const requestNow = useCallback(
		() =>
			Promise.allSettled([
				senderConsoleEnabled ? fetchSenderConsoleSummary() : Promise.resolve(null),
				senderAutoCampaignEnabled ? fetchSenderAutoCampaign() : Promise.resolve(null),
				senderPresetsEnabled ? fetchSenderPresets() : Promise.resolve<SenderRunPreset[]>([]),
				fetchPanelSenderSessions({ status: 'ready', limit: 1 }),
			] as const),
		[senderAutoCampaignEnabled, senderConsoleEnabled, senderPresetsEnabled],
	);

	const load = useCallback(async () => {
		setLoading(true);
		setError(false);
		const [summaryResult, autoResult, presetResult, sessionResult] = await requestNow();
		applyLoaded(summaryResult, autoResult, presetResult, sessionResult);
	}, [applyLoaded, requestNow]);

	useEffect(() => {
		let cancelled = false;
		void requestNow().then(results => {
			if (cancelled) return;
			const [summaryResult, autoResult, presetResult, sessionResult] = results;
			applyLoaded(summaryResult, autoResult, presetResult, sessionResult);
		});
		return () => {
			cancelled = true;
		};
	}, [applyLoaded, requestNow]);

	const busy = loading || runsLoading;
	const consoleRunning = summary?.status === 'running';
	const sentence = resolveNowSentence({
		consoleRunning,
		autoRunning,
		scenarioCount,
		readyAccountCount,
		runCount: runs.length,
		presetsEnabled: senderPresetsEnabled,
	});
	const action = resolveNowPrimaryAction({
		consoleRunning,
		autoRunning,
		scenarioCount,
		readyAccountCount,
		presetsEnabled: senderPresetsEnabled,
	});
	const firstVisit = isNowFirstVisit({
		scenarioCount,
		readyAccountCount,
		runCount: runs.length,
		presetsEnabled: senderPresetsEnabled,
	});
	const firstSteps = senderPresetsEnabled ? FIRST_STEPS_WITH_PRESETS : FIRST_STEPS_WITHOUT_PRESETS;
	const attention = resolveNowAttention({ queueFailed });
	const showPrimaryFooter = action.label !== 'Запустить';

	useLayoutEffect(() => {
		const before = prevRunsRef.current;
		const after = runs;
		if (before.length > 0) {
			const prepended = countPrependedRuns(before, after);
			const scrollEl = scrollRef.current;
			if (prepended > 0 && scrollEl && scrollEl.scrollTop > SCROLL_COMPENSATION_THRESHOLD) {
				scrollEl.scrollTop += measurePrependedRunsHeight(listAnchorRef.current, prepended);
			}
		}
		prevRunsRef.current = after;
	}, [runs]);

	function openAction() {
		const to = senderHref(action.to);
		if (action.to === '/console') {
			navigate(to, { state: { from: senderRoutes.hub } });
			return;
		}
		navigate(to);
	}

	function openRun(run: RunSummary) {
		navigate(`${senderRoutes.run}${run.id}`, { state: { from: senderRoutes.hub } });
	}

	if (error || runsError) {
		return (
			<SenderScreen title='Сейчас'>
				<SenderLoadError
					onRetry={() => {
						void load();
						void retryRuns();
					}}
				/>
			</SenderScreen>
		);
	}

	const heroIcon = consoleRunning ? RadioIcon : autoRunning ? ZapIcon : MoonIcon;

	return (
		<SenderScreen
			title='Сейчас'
			scrollRef={scrollRef}
			footer={
				showPrimaryFooter ? (
					<Button
						type='button'
						className={senderPrimaryButtonClassName()}
						disabled={busy}
						onClick={openAction}
					>
						{action.label}
					</Button>
				) : undefined
			}
		>
			{busy && !summary && runs.length === 0 ? (
				<div className='flex justify-center py-8'>
					<Spinner />
				</div>
			) : (
				<>
					<div className='flex flex-col gap-4'>
						<SenderHero icon={heroIcon}>{sentence}</SenderHero>
						<SenderMetricGrid
							metrics={[
								{ label: 'В очереди', value: formatNowCount(nowQueueCount(summary)), tone: 'muted' },
								{ label: 'Успех', value: formatNowCount(summary?.messagesSuccess), tone: 'ok' },
								{ label: 'Ошибки', value: formatNowCount(summary?.messagesErrors), tone: 'bad' },
							]}
						/>
						{firstVisit ? <SenderStepTimeline steps={firstSteps} /> : null}
						{attention ? (
							<SenderCallout onClick={() => navigate(senderHref(attention.to))}>
								{attention.label}
							</SenderCallout>
						) : null}
					</div>
					<div ref={listAnchorRef}>
						<SenderRunsVirtualFeed
							runs={runs}
							hasMore={hasMore}
							isFetchingMore={isFetchingMore}
							loadMoreError={loadMoreError}
							scrollRef={scrollRef}
							onLoadMore={() => void loadMore()}
							onRunClick={openRun}
						/>
					</div>
				</>
			)}
		</SenderScreen>
	);
}
